/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI
 * (C) Copyright IBM Corp. 2019
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import StringResources from 'nls/StringResources';
import ContentPanel from './schematic/ContentPanel';
import PackagePanel from './schematic/PackagePanel';
import ToastEnum from './schematic/ToastEnum';
import {
	Dialog,
	GlobalToast,
	Tabs,
	TabPanel
} from '@ba/ba-ui-carbon-toolkit';
import '../../stylesheets/all.scss';

// Schematic Editor class
export default class SchematicEditor extends Component {

	static propTypes = {
		definition: PropTypes.object.isRequired,
		okCallbackFn: PropTypes.func.isRequired,
		onCloseDialog: PropTypes.func,
		zipMaxSize: PropTypes.number,
		imgMaxSize: PropTypes.number
	}

	// Constructor
	// State consists of property to define whether or not the dialog is open or closed,
	// the selectedTab for the tab panel and the selected item in the select drop down list
	constructor(props) {
		super(props);

		this.addSvgFilesButtonRef = null;

		// need to clone the definition from input props
		let stateDefinition = this._cloneJsonObject( this.props.definition );

		this.state = {
			isDialogOpen: false,
			definition: stateDefinition,
			selectedIndices: [],
			search: '',
			searchMatches: null,
			toastMsg: null,
			svgFiles: null,
			selectedTab: 'schematicEditorContentTab',
			tabs: null
		};
	}

	/**
	* componentDidMount - Lifecycle method. Executes before first render. Executes when the element is inserted into the virtual DOM but before the HTML node itself can be manipulated.
	* Use this point to configure the element as required based on properties passed upon creation
	**/
	componentDidMount = () =>{
	}

	openDialog = () => {
		this.setState({ isDialogOpen: true });
	}

	applyDialog = () => {
		// normally just call the OK callback - but wrap in a function for any housekeeping
		this.props.okCallbackFn( this.state.definition );
		this.closeDialog();
	}

	closeDialog = () => {
		const { onCloseDialog } = this.props;
		this.setState({ isDialogOpen: false }, () => {
			if (onCloseDialog){
				onCloseDialog();
			}
		});
	}

	_cloneJsonObject = ( _object ) => {
		return JSON.parse( JSON.stringify( _object ) );
	}

	_renderGlobalToast = () => {
		const { toastMsg } = this.state;
		return toastMsg && toastMsg.type && toastMsg.info && (
			<GlobalToast
				statusType={toastMsg.type}
				timedDismiss={true}
				contentString={toastMsg.info}
				targetId='schematicEditorToastTargetId'
				onClose={ () => { this.setState( { toastMsg: null } ); } } >
			</GlobalToast>
		);
	}

	// Update the state when the package details(name or description) have changed
	_updatePackageInfo = (_result) => {
		var newDefinition = this._cloneJsonObject( this.state.definition );
		newDefinition.meta[_result.name] = _result.value;
		this.setState( {
			definition: newDefinition
		});
	}

	_handleContentChange = ( _result ) => {
		this.setState( _result );
	}

	_iconLoadFailed = ( _error ) => {
		this.setState( {
			toastMsg: {
				type: ToastEnum.ERROR,
				info: _error.message
			}
		} );
	}

	_handleIconLoad = ( _fileName, _blobUrl ) => {
		var newDefinition = this._cloneJsonObject( this.state.definition );
		newDefinition.meta.icon = _fileName;
		newDefinition.iconUrl = _blobUrl;

		var msg = _fileName ? StringResources.get('schematicsIconAdded', { file: _fileName } ) : StringResources.get('schematicsIconRemove');
		this.setState( {
			toastMsg: {
				type: ToastEnum.SUCCESS,
				info: msg
			},
			definition: newDefinition
		} );
	}

	// Function used to render the CustomPalette element (dialog)
	render() {
		let dialogTitle = StringResources.get('editSchematic');
		let contentTabTitle = StringResources.get('schematicsContentTab');
		let packageTabTitle = StringResources.get('schematicsPackageTab');

		const { zipMaxSize, imgMaxSize } = this.props;
		const { isDialogOpen, selectedTab , tabs, definition, svgFiles, selectedIndices, search, searchMatches } = this.state;

		return(
			isDialogOpen &&
			(
				<div>
					{this._renderGlobalToast()}
					<Dialog width='920px' onClose={ this.closeDialog } startingFocusIndex={0}>
						<Dialog.Header id='schematicEditorToastTargetId'>{dialogTitle}</Dialog.Header>
						<Dialog.Body>
							<Tabs
								draggable={false}
								selected={selectedTab}
								onChange={tabId => this.setState({ selectedTab: tabId }) }
								stateTabs={tabs}
								className='clsSVGEditorTabs'
							>
								<TabPanel id='schematicEditorPackageTab' label={packageTabTitle}>
									<PackagePanel
										packageName={definition.meta.name}
										packageDescription={definition.meta.description}
										iconName={definition.meta.icon}
										iconURL={definition.iconUrl}
										zipMaxSize={zipMaxSize}
										imgMaxSize={imgMaxSize}
										onChange={this._updatePackageInfo}
										onIconChanged={this._handleIconLoad}
										onIconError={this._iconLoadFailed}
									/>
								</TabPanel>
								<TabPanel id='schematicEditorContentTab' label={contentTabTitle}>
									<ContentPanel
										definition={definition}
										svgFiles={svgFiles}
										selectedIndices={selectedIndices}
										search={search}
										searchMatches={searchMatches}
										zipMaxSize={zipMaxSize}
										imgMaxSize={imgMaxSize}
										onChange={this._handleContentChange}
									/>
								</TabPanel>
							</Tabs>
						</Dialog.Body>
						<Dialog.Footer id='schematicEditorFooterId' >
							<Dialog.Button id='idCancelBtn' label={StringResources.get('cancelDialog')} onClick={ this.closeDialog } tabIndex={0} />
							<Dialog.Button id='idOkBtn' autofocus label={StringResources.get('saveDialog')} onClick={ this.applyDialog } tabIndex={0} primary />
						</Dialog.Footer>
					</Dialog>
				</div>
			)
		);
	}
}

SchematicEditor.defaultProps = {
	zipMaxSize: 500,
	imgMaxSize: 500
};



// WEBPACK FOOTER //
// ./src/js/dialog/SchematicEditor.js